import React, { useMemo, useCallback } from "react";
import dayjs from "dayjs";
import LineChart from "./charts/LineChart";
import useMultiHealthApis from "./hooks/useMultiHealthApis";
import { useAccount } from './account-context';
import { getApiPaths } from "./constants/healthEndpoints";
import {
  ensurePct,
  clamp,
  toArray,
  mapStoreScore,
  parseDate,
  downsample,
  mapPercent,
  mapServicePercent,
  formatAlerts,
  pickUnhealthyCount
} from "./charts/chartUtils";

/* ======= STATIC CONFIG (no props) ======= */
const DAYS = 30;

// const BASE_URL = "https://xbzpe5g7i2ibwqyuwlw2uszjva0zhtqq.lambda-url.us-east-1.on.aws"; // optional override
const DEFAULT_COLORS = ["#5a6fa3"];


export default function DisplayStoreLineChart() {
  const { accountId } = useAccount();
  const API_URL_PATHS = getApiPaths(accountId);
const { MARKET_STORE_HEALTH } = API_URL_PATHS;

  const ENDPOINTS = {
  MarketStoreHealth: MARKET_STORE_HEALTH,
};
  const { dataByKey, loading, errorsByKey } = useMultiHealthApis({
    endpoints: ENDPOINTS,
    // baseUrl: BASE_URL, // uncomment to override the default base URL in the hook
    retries: 1,
    timeout: 10000,
  });

  // Build alternate-day labels (e.g., "sep 29", "oct 1", "oct 3", …) and one aligned series
  const computed = useMemo(() => {
    // Build date -> count map
    const map = new Map(); // iso (YYYY-MM-DD) -> count

    const arr = toArray(dataByKey?.MarketStoreHealth);
    //console.log("Raw store health data array:", arr);
    for (const item of arr) {
      const d = parseDate(item);
      if (!d) continue;
      const iso = d.format("YYYY-MM-DD");
      const val = pickUnhealthyCount(item);
      if (val !== null) map.set(iso, val);
    }

    // Sort dates and keep the last N
    const allDates = Array.from(map.keys()).sort();
    const displayDates = allDates.slice(-DAYS);

    // Down-sample to alternate days
    //const altDates = downsample(lastDates, 2);

    // Labels like "sep 29", "oct 1", ...
    const labels = displayDates.map((iso) => dayjs(iso).format("MMM D").toLowerCase());

    // Data aligned to labels (null for gaps)
    const data = displayDates.map((iso) => (map.has(iso) ? map.get(iso) : null));

    const series = [
      {
        name: "Alerts",
        data,
        smooth: false,
        area: false,
        lineStyle: { width: 2 },
        showSymbol: true,
        symbol: "circle",
        symbolSize: 6,
        emphasis: { focus: "series" },
      },
    ];

    return { labels, series };
  }, [dataByKey]);

  const handlePointClick = useCallback((p) => {
    if (!p) return;
    // Optional: drilldown/navigation
    // console.log("Clicked", {
    //   label: p.name,
    //   seriesName: p.seriesName,
    //   value: p.value,
    //   dataIndex: p.dataIndex,
    // });
  }, []);

  // Loading UI (no JSX)
  if (loading) {
    return React.createElement(
      "section",
      { className: "chart-card", "aria-label": "Store health this month" },
      React.createElement("h3", null, "Stores Health by Day (Alerts)"),
      React.createElement(
        "div",
        { className: "chart-container", role: "region", "aria-roledescription": "line chart" },
        React.createElement("div", null, "Loading store health…")
      )
    );
  }

  // Error UI (no JSX)
  if (Object.keys(errorsByKey || {}).length > 0) {
    return React.createElement(
      "section",
      { className: "chart-card", "aria-label": "Store health this month" },
      React.createElement("h3", null, "Stores Health by Day (Alerts)"),
      React.createElement(
        "div",
        { className: "chart-container", role: "region", "aria-roledescription": "line chart" },
        React.createElement("div", { style: { color: "red" } }, "Failed to load store health")
      )
    );
  }

  



  // Render LineChart (no JSX)
  return React.createElement(
    "section",
    { className: "chart-card", "aria-label": "Store health this month" },
    React.createElement("h3", null, "Stores Health by Day"),
    React.createElement(
      "div",
      { className: "chart-container", role: "region", "aria-roledescription": "line chart" },
      React.createElement(LineChart, {
        title: "",
        labels: computed.labels,      
        series: computed.series,      
        unit: "Count",
         yAxes: [
          {
            name: "Alert",
            unit: "count",
            color: "#5a6fa3",
            position: "left",
            min: 0,               // explicit numeric min
          //  max: 100,     // explicit numeric max (with buffer)
            integer: true,               // force integer ticks / labels
          },
        ],
        yMinBuffer: 10,
        smooth: true,
        legendPosition: "top",
        showLegend: true,
        showZoom: false,
        height: 240,
        colors: DEFAULT_COLORS,
        onPointClick: handlePointClick,
        
   tooltipFormatter: (params) => {
          // params may be array (axis trigger) or single object
          // const p = Array.isArray(params) ? params[0] : params;
          // const dateLabel = p?.axisValueLabel ?? p?.name ?? "";
          // const value = p?.value ?? "—";
          // return `<div style="margin-bottom:6px;"><strong>${dateLabel}</strong></div><div>Store Health : <strong>${value} Alert</strong></div>`;
          const p = Array.isArray(params) ? params[0] : params;
          const dateLabel = p?.axisValueLabel ?? p?.name ?? "";
          const rawValue = Array.isArray(p?.value) ? p.value[1] : p?.value;
          const valueText = formatAlerts(rawValue);      
          return `
              <div style="margin-bottom:6px;"> ${dateLabel}</div>
              <div>
                Store Health : <strong>${valueText}</strong>
              </div>
            `;
        },

      })
    )
  );
}